import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, useAuth } from "../auth/AuthContext";

/**
 * The front door for a guest invite link (KEY-297): /guest/<token>. Opening it asks the server to email a
 * one-time code to the invited inbox — the inbox, not the link, is the credential, so a forwarded link
 * stops right here. Every failure shows the same message: a dead link, a wrong code and an expired invite
 * are deliberately indistinguishable from this side of the screen.
 */
export function GuestLanding() {
  const location = useLocation();
  const navigate = useNavigate();
  const { enterGuestSession } = useAuth();

  const token = location.pathname.split("/")[2] ?? "";
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resent, setResent] = useState(false);
  const landed = useRef(false);

  // One land per mount: it triggers the code email. React StrictMode double-mounts in dev; the ref (and
  // the server's per-email cooldown) keep that from burning two codes.
  useEffect(() => {
    if (landed.current || !token) return;
    landed.current = true;
    void api.guestLand(token);
  }, [token]);

  async function verify() {
    if (busy || code.trim().length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const session = await api.guestVerify(token, code.trim());
      await enterGuestSession(session);
      // KEY-916: an allocation's invite names the task (?section=&segment=&item=) — land them on it.
      const target = new URLSearchParams(location.search);
      const keep = new URLSearchParams();
      for (const k of ["section", "segment", "item"]) {
        const v = target.get(k);
        if (v) keep.set(k, v);
      }
      const query = keep.toString();
      navigate(`/instances/${session.instanceId}${query ? `?${query}` : ""}`, { replace: true });
    } catch {
      setError("That code wasn't recognised. Check the most recent email, or ask for a new code below.");
      setCode("");
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setResent(false);
    setError(null);
    await api.guestLand(token);
    setResent(true);
  }

  return (
    <main
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--bg)",
        padding: 20,
      }}
    >
      <div
        style={{
          width: "min(420px, 100%)",
          background: "var(--surface)",
          border: "var(--border-w) solid var(--border)",
          borderRadius: "var(--radius)",
          padding: "32px 28px",
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        <h1 style={{ margin: 0, fontSize: 20, color: "var(--text)" }}>You&rsquo;ve been invited to a Key Dial</h1>
        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, color: "var(--text-muted)" }}>
          We&rsquo;ve emailed a one-time code to the address this invite was sent to. Enter it below to open
          the dial.
        </p>

        <input
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          onKeyDown={(e) => {
            if (e.key === "Enter") void verify();
          }}
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          placeholder="6-digit code"
          aria-label="One-time code"
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: "12px 14px",
            fontSize: 20,
            letterSpacing: "0.35em",
            textAlign: "center",
            borderRadius: "var(--radius-sm)",
            border: "var(--border-w) solid var(--border)",
            background: "var(--surface-2)",
            color: "var(--text)",
            outline: "none",
          }}
        />

        {error && <p style={{ margin: 0, fontSize: 13, color: "var(--st-overdue)" }}>{error}</p>}
        {resent && !error && (
          <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>
            If this invite is still active, a new code is on its way.
          </p>
        )}

        <button
          className="kd-btn-primary"
          onClick={() => void verify()}
          disabled={busy || code.length < 6}
          style={{ justifyContent: "center", opacity: busy || code.length < 6 ? 0.6 : 1 }}
        >
          {busy ? "Checking…" : "Open the dial"}
        </button>

        <button
          onClick={() => void resend()}
          style={{
            border: "none",
            background: "none",
            cursor: "pointer",
            font: "inherit",
            fontSize: 13,
            color: "var(--text-faint)",
            textDecoration: "underline",
            padding: 4,
          }}
        >
          Send a new code
        </button>
      </div>
    </main>
  );
}
